import { makeAutoObservable, runInAction } from 'mobx';
import { RootStore } from '../../RootStore';
import { personService } from '../../../services/modules/person/person.service';
import { IPerson, PersonFormValues } from '../../../models/person/PersonModel';
import { GridApi } from 'ag-grid-community';
import { toast } from 'react-toastify';

interface ODataParams {
  $filter?: string;
  $orderby?: string;
  $skip?: number;
  $top?: number;
  $select?: string;
  $count?: boolean;
}

interface GridParams {
  startRow: number;
  endRow: number;
  filterModel: any;
  sortModel: any;
}

export class PersonStore {
  rootStore: RootStore;
  personList: IPerson[] = [];
  gridApi: GridApi<IPerson> | null = null;
  selectedPersonToEdit: IPerson | null = null;
  selectedPersonToDelete: number | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  setGridApi = (api: GridApi<IPerson>) => {
    this.gridApi = api;
  };

  refreshGrid = () => {
    if (this.gridApi) {
      this.gridApi.refreshInfiniteCache();
    }
  };

  refreshGridWithCurrentSettings = () => {
    if (this.gridApi) {
      this.gridApi.refreshInfiniteCache();
    }
  };

  listAllPersons = async (
    params?: GridParams
  ): Promise<{ rows: IPerson[]; lastRow: number; count: number }> => {
    try {
      this.rootStore.uiStore.setLoading(true);

      if (params) {
        const odataParams: ODataParams = {
          $count: true,
          $skip: params.startRow,
          $top: params.endRow - params.startRow,
        };

        if (params.filterModel) {
          const filters = Object.entries(params.filterModel).map(
            ([field, filter]: [string, any]) => {
              const numericFields = ['id', 'hospitalId'];
              if (numericFields.includes(field)) {
                return `${field} eq ${filter.filter}`;
              }
              const value = String(filter.filter ?? '').toLowerCase().replace(/'/g, "''");
              return `contains(tolower(${field}), '${value}')`;
            }
          );

          if (filters.length > 0) {
            odataParams.$filter = filters.join(' and ');
          }
        }

        if (params.sortModel && params.sortModel.length > 0) {
          const { colId, sort } = params.sortModel[0];
          odataParams.$orderby = `${colId} ${sort === 'asc' ? 'asc' : 'desc'}`;
        }

        const response: any = await personService.listAllPersons({
          params: odataParams,
        });

        return runInAction(() => {
          if (response) {
            const totalCount = response['@odata.count'] || 0;
            const persons = response.value || [];
            this.personList = persons;
            return {
              rows: persons,
              lastRow: totalCount,
              count: totalCount,
            };
          }
          return {
            rows: [],
            lastRow: 0,
            count: 0,
          };
        });
      } else {
        const response: any = await personService.listAllPersons();

        runInAction(() => {
          this.personList = Array.isArray(response) ? response : [];
        });

        return {
          rows: this.personList,
          lastRow: this.personList.length,
          count: this.personList.length,
        };
      }
    } catch (error) {
      console.error('Error fetching persons:', error);
      runInAction(() => {
        this.rootStore.uiStore.setError(
          error instanceof Error ? error.message : 'An unknown error occurred'
        );
      });
      throw error;
    } finally {
      runInAction(() => {
        this.rootStore.uiStore.setLoading(false);
      });
    }
  };

  getPersonsByHospitalId = async (hospitalId: number): Promise<IPerson[]> => {
    try {
      this.rootStore.uiStore.setLoading(true);
      const response = await personService.getPersonsByHospitalId(hospitalId);
      return runInAction(() => {
        return Array.isArray(response) ? response : [];
      });
    } catch (error) {
      console.error('Error fetching persons by hospital ID:', error);
      runInAction(() => {
        this.rootStore.uiStore.setError(
          error instanceof Error ? error.message : 'An unknown error occurred'
        );
      });
      return [];
    } finally {
      runInAction(() => {
        this.rootStore.uiStore.setLoading(false);
      });
    }
  };

  createPerson = async (values: PersonFormValues) => {
    try {
      this.rootStore.uiStore.setLoading(true);
      const response = await personService.createPerson(values);

      if (response.error != true) {
        this.refreshGridWithCurrentSettings();
      }
      return response;
    } catch (error) {
      console.error('Error creating person:', error);
      runInAction(() => {
        this.rootStore.uiStore.setError(
          error instanceof Error ? error.message : 'An unknown error occurred'
        );
        toast.error('Kişi oluşturulurken bir hata oluştu');
      });
      throw error;
    } finally {
      runInAction(() => {
        this.rootStore.uiStore.setLoading(false);
      });
    }
  };

  updatePerson = async (id: number, values: PersonFormValues) => {
    try {
      this.rootStore.uiStore.setLoading(true);
      const response = await personService.updatePerson(id, values);

      if (response.error === false) {
        this.refreshGridWithCurrentSettings();
      } else {
        toast.error('Kişi güncellenemedi. ' + response.message);
      }
      return response;
    } catch (error) {
      console.error('Error updating person:', error);
      toast.error('Kişi güncellenirken bir hata oluştu');
      throw error;
    } finally {
      this.rootStore.uiStore.setLoading(false);
    }
  };

  deletePerson = async (id: number) => {
    try {
      this.rootStore.uiStore.setLoading(true);
      const response = await personService.deletePerson(id);
      if (response.error == false) {
        toast.success('Kişi başarıyla silindi.');
        this.refreshGridWithCurrentSettings();
      } else {
        toast.error('Kişi silinemedi. ' + response.message);
      }
      return response;
    } catch (error) {
      console.error('Kişi silinirken hata:', error);
      toast.error('Kişi silinirken bir hata oluştu');
      throw error;
    } finally {
      this.rootStore.uiStore.setLoading(false);
    }
  };

  setSelectedPersonToEdit = (person: IPerson | null) => {
    this.selectedPersonToEdit = person;
  };

  setSelectedPersonToDelete = (id: number | null) => {
    this.selectedPersonToDelete = id;
  };
}

